import React, { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Ban, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CUSTOMER_OK = ["pending_payment", "paid", "confirmed", "assigned"];
const ADMIN_OK = [...CUSTOMER_OK, "picked_up", "in_transit"];
const AFTER_PICKUP = ["picked_up", "in_transit"];

export function canCancelOrder(user, order) {
  if (!user || !order || order.kind === "paint") return false;
  if (user.role === "admin") return ADMIN_OK.includes(order.status);
  if (user.role === "customer") return order.customer_id === user.id && CUSTOMER_OK.includes(order.status);
  return false;
}

function refundSummary(order, isAdmin) {
  const p = order.payment || {};
  if (order.status === "pending_payment" || !p.status || p.status === "unpaid") return "Nothing has been charged, so there's nothing to refund.";
  if (p.status === "paid" && (p.provider === "stripe" || p.provider === "stripe_mock")) {
    const charged = p.amount_charged ?? p.amount ?? order.price?.total ?? 0;
    const credit = p.credit_applied || 0;
    return `$${Number(charged).toFixed(2)} will be refunded to the card automatically${credit ? ` and $${Number(credit).toFixed(2)} store credit returned` : ""}.`;
  }
  if (p.status === "paid" && p.provider === "account_credit") return `$${Number(p.credit_applied || p.amount || 0).toFixed(2)} store credit will be returned to the account.`;
  if (p.status === "on_account") return p.billed ? `This job was already invoiced${isAdmin ? " — a Xero credit note will be raised against that invoice" : " — the charge will be reversed on your account"}.` : "Nothing will be billed to the account for this job.";
  return "";
}

export default function CancelOrderDialog({ order, user, open, onOpenChange, onCancelled, trigger = true }) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const isOpen = open ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  if (!order || !canCancelOrder(user, order)) return null;
  const isAdmin = user.role === "admin";
  const afterPickup = AFTER_PICKUP.includes(order.status);
  const reasonOk = reason.trim().length >= 3;

  const confirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/cancel`, { reason });
      const r = data.refund || {};
      let msg = `${order.tracking_number} cancelled`;
      if (r.card_refund) msg += ` — $${r.card_refund.toFixed(2)} refunded to card`;
      if (r.credit_returned) msg += ` — $${r.credit_returned.toFixed(2)} credit returned`;
      if (r.status === "refund_failed") toast.warning(`${msg}. Card refund failed: ${r.error || "see Stripe"}`); else toast.success(msg);
      setOpen(false);
      setReason("");
      onCancelled?.(data.order);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't cancel"); } finally { setBusy(false); }
  };

  return (
    <>
      {trigger && (
        <Button variant="outline" onClick={() => setOpen(true)} data-testid="cancel-order-button" className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-800">
          <Ban className="h-4 w-4" /> Cancel order
        </Button>
      )}
      <Dialog open={isOpen} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="cancel-order-dialog">
          <DialogHeader>
            <DialogTitle>Cancel {order.tracking_number}?</DialogTitle>
            <DialogDescription>
              {order.pickup?.city} → {order.dropoff?.city}{order.customer_name && isAdmin ? ` · ${order.customer_name}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            {afterPickup && <p className="rounded-md border border-amber-300 bg-amber-50 p-2 text-amber-900" data-testid="cancel-after-pickup-warning">This parcel is already on board with {order.driver_name || "the driver"} — they'll be told to bring it back to the depot.</p>}
            <p className="rounded-md bg-secondary/60 p-2" data-testid="cancel-refund-summary">{refundSummary(order, isAdmin)}</p>
            {order.driver_name && !afterPickup && order.status === "assigned" && <p className="text-xs text-muted-foreground">{order.driver_name} will be notified it's off their run.</p>}
            <div>
              <label className="text-xs font-medium">Reason for cancelling <span className="text-red-700">*</span></label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={300} placeholder={isAdmin ? "e.g. Customer no longer needs it / couldn't be fulfilled" : "e.g. No longer needed / booked by mistake"} data-testid="cancel-reason-input" className="mt-1" />
              {!reasonOk && <p className="mt-1 text-xs text-muted-foreground" data-testid="cancel-reason-hint">A reason is required.</p>}
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy} data-testid="cancel-order-keep">Keep order</Button>
            <Button onClick={confirm} disabled={busy || !reasonOk} data-testid="cancel-order-confirm" className="gap-2 bg-red-700 text-white hover:bg-red-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4" />} Yes, cancel it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
